import React, { useEffect, useState } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { Row, Button, Tooltip, Form, Select, Modal } from "antd";
import MaskedInput from "antd-mask-input";
import moment from "moment";
import { PlusCircleFilled } from "@ant-design/icons";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import { toDisplayFormat, toHttpFormat, toHttpFormatStartOfDay } from "../../lib/utils/formatDate";
import { PhoneType } from "../../lib/interfaces/PhoneType";
import { useAccountInfoContainerContext } from "../../containers/AccountInfoContainer/AccountInfoContainerContext";
import { Phone } from "../../lib/interfaces/Phone";
import LocalStorageService from "../../services/LocalStorageService";
import { checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { MENU_ACCOUNT_ACCOUNTINFO_WRITE } from "../../lib/const/permissions";
import { hasAccess } from "../RolesModule/hasAccess";
import usePhoneMask from "../../lib/hooks/usePhoneMask";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import ChangeVersionsTable from "../common/ChangeVersionsTable";

const IndividualPhonesTable = () => {
  const {
    getAccountPhonesVersions,
    createIndividualPhone,
    updateAccountPhone,
    closeAccountPhone,
  } = useAccountInfoContainerContext();
  const {
    accountPhones,
    accountPhonesLoading,
    accountPhonesVersions,
    accountPhonesVersionsLoading,
  } = useSelector((state: RootStateOrAny) => state.accounts);
  const phoneTypes: PhoneType[] = useSelector((state: RootStateOrAny) => state.dicts.phoneTypes);
  const [tableData, setTableData] = useState<any>([]);
  const [childTableData, setChildTableData] = useState<any>([]);
  // console.log({ tableData });
  const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);

  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);

  const [createForm] = Form.useForm();
  const [tableForm] = Form.useForm();
  
  const {
    phoneMask,
    getCountryCodeToDisplay,
    getPhoneToDisplay,
    cleanStringNumber,
  } = usePhoneMask();

  const handleSetInitialValue = (field) => {
    setTimeout(() => {
      tableForm.setFieldsValue(field);
    }, 0);
  };

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    countryCode: "10%",
    phone: "20%",
    phoneType: "",
  };

  const [columns, setColumns] = useState([
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: columnsWidths.id,
      hidden: true,
    },
    {
      title: "Код страны",
      dataIndex: "countryCode",
      key: "countryCode",
      width: columnsWidths.countryCode,
    },
    {
      title: "Номер",
      dataIndex: "phone",
      key: "phone",
      width: columnsWidths.phone,
    },
    {
      title: "Тип номера",
      dataIndex: "phoneType",
      key: "phoneType",
      width: columnsWidths.phoneType,
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render: (text, record) =>
        `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`,
    },
    {
      key: "actions",
      width: columnsWidths.actions,
    },
    {
      key: "buttons",
      width: columnsWidths.buttons,
    },
  ]);

  const getChildColumns = () => [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: columnsWidths.id,
      hidden: true,
    },
    {
      title: "Код страны",
      dataIndex: "countryCode",
      key: "countryCode",
      width: columnsWidths.countryCode,
      render: (countryCode, record) => {
        if (record.isCreated || record.isEditing) {
          handleSetInitialValue({ countryCode });
          return (
            <Form.Item
              name="countryCode"
              className="form-item-required__hidden-error__mb0"
              rules={[{ required: true }]}
            >
              <MaskedInput mask={phoneMask.country} name="countryCode" allowClear />
            </Form.Item>
          );
        }
        return countryCode;
      },
    },
    {
      title: "Номер",
      dataIndex: "phone",
      key: "phone",
      width: columnsWidths.phone,
      render: (phone, record) => {
        if (record.isCreated || record.isEditing) {
          handleSetInitialValue({ phone });
          return (
            <Form.Item
              name="phone"
              className="form-item-required__hidden-error__mb0"
              rules={[{ required: true }]}
            >
              <MaskedInput mask={phoneMask.phone} name="phone" allowClear />
            </Form.Item>
          );
        }
        return phone;
      },
    },
    {
      title: "Тип номера",
      dataIndex: "phoneType",
      key: "phoneType",
      width: columnsWidths.phoneType,
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render: (v, record) => (
        <ChangeVersionsTable.Period
          record={record}
          setFieldsValue={tableForm.setFieldsValue}
          fromDateRequired
          toDateRequired
          disabledToDate={(current) =>
            current.isBefore(record.fromDate, "day") || current.isAfter(record.toDate, "day")
          }
        />
      ),
    },
    {
      key: "actions",
      width: columnsWidths.actions,
      render: (value, record) => {
        const isHasAccess = hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE);
        return (
          <>
            <ChangeVersionsTable.AddButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.EditButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.CloseButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.SubmitButton record={record} submit={tableForm.submit} />
            <ChangeVersionsTable.CancelButton
              record={record}
              resetFields={tableForm.resetFields}
              setChildTableData={setChildTableData}
            />
          </>
        );
      },
    },
    {
      key: "buttons",
      width: columnsWidths.buttons,
    },
  ];

  const [childColumns, setChildColumns] = versionsTableUtils.useChildColumns(getChildColumns, [
    isChangingForbidden,
  ]);

  // // Дизейбл функциональных кнопок, в режиме изменения
  // useEffect(() => {
  //   const newActionsColumn = getChildColumns().find(({ key }) => key === "actions");
  //   setChildColumns((prevState) =>
  //     prevState.map((column) => {
  //       if (column.key === "actions") {
  //         return { ...column, render: newActionsColumn?.render };
  //       }
  //       return column;
  //     })
  //   );
  // }, [isChangingForbidden]);

  useEffect(() => {
    setTableData(
      accountPhones
        .map((item) => ({
          ...item,
          key: item.id,
          phone: getPhoneToDisplay(item.phone),
          countryCode: getCountryCodeToDisplay(item.countryCode),
          phoneType: phoneTypes.find(({ id }) => String(id) === String(item.phoneTypeId))?.name,
        }))
        .sort(versionsTableUtils.sortVersionsByDate)
    );
  }, [accountPhones, phoneTypes]);

  useEffect(() => {
    setChildTableData(
      accountPhonesVersions
        .map((version, i) => ({
          ...version,
          phone: getPhoneToDisplay(version.phone),
          countryCode: getCountryCodeToDisplay(version.countryCode),
          phoneType: phoneTypes.find(({ id }) => String(id) === String(version.phoneTypeId))?.name,
          ...versionsTableUtils.getCommonChildTableProps(version, i, accountPhonesVersions.length),
        }))
        .sort(versionsTableUtils.sortVersionsByDate)
    );
  }, [accountPhonesVersions, phoneTypes]);

  const renderCreateModal = () => {
    const closeCreateModal = () => {
      setCreateModalVisible(false);
      createForm.resetFields();
    };
    const onFinishCreate = (values) => {
      const phoneNumber = values.phoneNumber.replace(/\D/g, "");
      const countryCode = `+${values.countryCode?.replace(/\D/g, "")}`;
      const tenantId = Number(LocalStorageService.getTenantId());
      const phone: Phone = {
        fromDate: toHttpFormat(moment()),
        phoneTypeId: values.phoneTypeId,
        countryCode,
        phone: phoneNumber,
        dsc: "",
        tenantId,
      };
      createIndividualPhone({ phone });
      closeCreateModal();
    };
    return (
      <Modal
        title="Номер телефона"
        okText="Сохранить"
        visible={createModalVisible}
        onCancel={closeCreateModal}
        footer={[
          <Button key="back" onClick={closeCreateModal}>
            Отмена
          </Button>,
          <Button key="submit" type="primary" onClick={createForm.submit}>
            Сохранить
          </Button>,
        ]}
      >
        <Form
          form={createForm}
          initialValues={{
            exiteId: "",
            date: "",
          }}
          onFinish={onFinishCreate}
          labelCol={{ span: 6, offset: 3 }}
        >
          <Form.Item
            label="Код страны"
            name="countryCode"
            className="form-item-required__hidden-error"
            wrapperCol={{ span: 4 }}
            rules={[{ required: true }]}
          >
            <MaskedInput mask={phoneMask.country} name="phone" allowClear />
          </Form.Item>
          <Form.Item
            label="Номер"
            name="phoneNumber"
            rules={[{ required: true, message: "Поле обязательно для заполнения" }]}
            className="form-item-required__hidden-error"
            wrapperCol={{ span: 8 }}
          >
            <MaskedInput mask={phoneMask.phone} name="phoneNumber" allowClear />
          </Form.Item>
          <Form.Item
            className="form-item-required__hidden-error__mb0"
            initialValue={1}
            name="phoneTypeId"
            label="Тип номера"
            wrapperCol={{ span: 10 }}
          >
            <Select>
              {phoneTypes.map(({ id, name }) => (
                <Select.Option value={id} key={id}>
                  {name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  const onFinish = (values) => {
    const version = childTableData.find(
      (item) => item.isCreated || item.isEditing || item.isClosing
    );
    if (version.isClosing) {
      closeAccountPhone({ fromDate: values.toDate, phoneId: version.id });
    } else {
      const isForceUpdate = Boolean(version.isEditing);
      const versionDate = isForceUpdate
        ? version.fromDate
        : toHttpFormatStartOfDay(moment(values.fromDate));

      const phone: Phone = {
        id: version.id,
        fromDate: versionDate,
        toDate: version.toDate,
        countryCode: cleanStringNumber(values.countryCode),
        phone: cleanStringNumber(values.phone),
        phoneTypeId: version.phoneTypeId,
        tenantId: version.tenantId,
        dsc: version.dsc,
      };

      // return console.log({ phone });

      updateAccountPhone({
        phoneId: version.id,
        fromDate: versionDate,
        force: isForceUpdate,
        phone,
      });
    }
  };

  return (
    <>
      {renderCreateModal()}
      <Form form={tableForm} onFinish={onFinish}>
        <ToggleableVersionsTable
          style={{ marginTop: "45px" }}
          title={
            <Row align="middle">
              <h4 style={{ margin: 0 }}>Номер телефона</h4>
              <Tooltip title="Добавить номер телефона">
                <Button
                  style={{ color: "#00BB77", marginLeft: "15px", border: "none" }}
                  shape="circle"
                  icon={<PlusCircleFilled />}
                  onClick={() => setCreateModalVisible(true)}
                />
              </Tooltip>
            </Row>
          }
          tableName="individual-phones-table"
          columns={columns}
          setColumns={setColumns}
          childTableColumns={childColumns}
          setChildTableColumns={setChildColumns}
          tableData={tableData}
          isTableLoading={checkIsLoading(accountPhonesLoading)}
          childTableData={childTableData}
          isChildTableLoading={checkIsLoading(accountPhonesVersionsLoading)}
          isPageable={false}
          onOpenVersionsCallback={({ id }) => {
            getAccountPhonesVersions(id);
          }}
          isToggleableColumns={false}
        />
      </Form>
    </>
  );
};

export default IndividualPhonesTable;
